import type { iLedger } from "@sdk/ledgers/ledgers-models";
import { LEDGER_TYPE_LABEL } from "@sdk/ledgers/ledgers-models";
import { getWorkspacePaymentAccountLedgerIds } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";
import type { iOrganizationDefaultLedgerAccounts } from "@sdk/default-ledger-accounts/default-ledger-accounts-model";

export type LedgerOwnershipGroup =
  | "WORKSPACE_DEFAULT"
  | "BRAND_DEFAULT"
  | "SUPPLIER_DEFAULT"
  | "SUPPLIER_PAYMENT_METHOD_DEFAULT"
  | "CUSTOMER_PAYMENT_METHOD_DEFAULT"
  | "CUSTOM";

export const LEDGER_OWNERSHIP_GROUP_LABEL: Record<LedgerOwnershipGroup, string> =
  {
    WORKSPACE_DEFAULT: "Workspace default ledgers",
    BRAND_DEFAULT: "Brand default ledgers",
    SUPPLIER_DEFAULT: "Supplier default ledgers",
    SUPPLIER_PAYMENT_METHOD_DEFAULT: "Supplier payment method default ledgers",
    CUSTOMER_PAYMENT_METHOD_DEFAULT: "Customer payment method default ledgers",
    CUSTOM: "Custom ledgers",
  };

export const LEDGER_OWNERSHIP_GROUP_ORDER: readonly LedgerOwnershipGroup[] = [
  "WORKSPACE_DEFAULT",
  "BRAND_DEFAULT",
  "SUPPLIER_DEFAULT",
  "SUPPLIER_PAYMENT_METHOD_DEFAULT",
  "CUSTOMER_PAYMENT_METHOD_DEFAULT",
  "CUSTOM",
];

export function isCustomLedger(ledger: iLedger): boolean {
  return ledger.isSystemDefault !== true;
}

export function isWorkspaceDefaultLedger(ledger: iLedger): boolean {
  return (
    ledger.isSystemDefault === true && ledger.boundEntityType === "ORGANIZATION"
  );
}

export function isTradeExpenseSelectableLedger(ledger: iLedger): boolean {
  return isCustomLedger(ledger) || isWorkspaceDefaultLedger(ledger);
}

/** Workspace-provisioned Main Bank Account and Cash ledgers (BANK_ACCOUNT category). */
export function isWorkspaceDefaultPaymentAccountLedger(
  ledger: iLedger
): boolean {
  if (!isWorkspaceDefaultLedger(ledger)) {
    return false;
  }

  if (ledger.accountCategory === "BANK_ACCOUNT") {
    return true;
  }

  const name = ledger.name?.trim();
  return name === "Main Bank Account" || name === "Cash";
}

/** Custom ledgers plus workspace Main Bank Account / Cash for payment method linking. */
export function isPaymentAccountSelectableLedger(
  ledger: iLedger,
  workspacePaymentAccountLedgerIds?: ReadonlySet<string>
): boolean {
  if (isCustomLedger(ledger)) {
    return true;
  }

  if (
    ledger.id &&
    workspacePaymentAccountLedgerIds?.has(ledger.id)
  ) {
    return true;
  }

  return isWorkspaceDefaultPaymentAccountLedger(ledger);
}

export function resolveWorkspacePaymentAccountLedgerIds(
  accounts?: iOrganizationDefaultLedgerAccounts | null
): Set<string> {
  return getWorkspacePaymentAccountLedgerIds(accounts);
}

export function getLedgerOwnershipGroup(ledger: iLedger): LedgerOwnershipGroup {
  if (ledger.isSystemDefault === true) {
    switch (ledger.boundEntityType) {
      case "ORGANIZATION":
        return "WORKSPACE_DEFAULT";
      case "BRAND":
        return "BRAND_DEFAULT";
      case "SUPPLIER":
        return "SUPPLIER_DEFAULT";
      case "SUPPLIER_PAYMENT_METHOD":
        return "SUPPLIER_PAYMENT_METHOD_DEFAULT";
      case "CUSTOMER_PAYMENT_METHOD":
        return "CUSTOMER_PAYMENT_METHOD_DEFAULT";
    }
  }
  return "CUSTOM";
}

export function normalizeLedgerCurrency(ledger: iLedger): string {
  return ledger.currency?.trim().toUpperCase() || "—";
}

export function normalizeLedgerCodeDisplay(code?: string | null): string | undefined {
  const trimmed = code?.trim();
  return trimmed || undefined;
}

export function formatLedgerDisplayName(
  ledger: Pick<iLedger, "code" | "name" | "id">
): string {
  const name = ledger.name?.trim() || ledger.id || "Ledger";
  const code = normalizeLedgerCodeDisplay(ledger.code);
  return code ? `${code} · ${name}` : name;
}

export function formatLedgerOptionLabel(ledger: iLedger): string {
  const parts = [formatLedgerDisplayName(ledger)];
  if (ledger.currency) {
    parts.push(`(${ledger.currency})`);
  }
  if (ledger.type) {
    parts.push(`· ${LEDGER_TYPE_LABEL[ledger.type]}`);
  }
  if (ledger.archived) {
    parts.push("(archived)");
  }
  return parts.join(" ");
}

export function sortLedgersByName(ledgers: iLedger[]): iLedger[] {
  return [...ledgers].sort((a, b) => {
    const codeA = normalizeLedgerCodeDisplay(a.code) ?? "";
    const codeB = normalizeLedgerCodeDisplay(b.code) ?? "";
    if (codeA && codeB) {
      const codeCompare = codeA.localeCompare(codeB, undefined, { numeric: true });
      if (codeCompare !== 0) {
        return codeCompare;
      }
    } else if (codeA && !codeB) {
      return -1;
    } else if (!codeA && codeB) {
      return 1;
    }
    return (a.name ?? "").localeCompare(b.name ?? "");
  });
}

export type LedgerOwnershipGroupSection = {
  ownershipGroup: LedgerOwnershipGroup;
  ledgers: iLedger[];
};

export type LedgerCurrencyOwnershipGroups = {
  currency: string;
  groups: LedgerOwnershipGroupSection[];
};

export function groupLedgersByCurrencyAndOwnership(
  ledgers: iLedger[]
): LedgerCurrencyOwnershipGroups[] {
  const byCurrency = new Map<string, Map<LedgerOwnershipGroup, iLedger[]>>();

  for (const ledger of ledgers) {
    const currency = normalizeLedgerCurrency(ledger);
    const ownership = getLedgerOwnershipGroup(ledger);
    if (!byCurrency.has(currency)) {
      byCurrency.set(currency, new Map());
    }
    const ownershipMap = byCurrency.get(currency)!;
    const list = ownershipMap.get(ownership) ?? [];
    list.push(ledger);
    ownershipMap.set(ownership, list);
  }

  return [...byCurrency.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, ownershipMap]) => ({
      currency,
      groups: LEDGER_OWNERSHIP_GROUP_ORDER.map((ownershipGroup) => ({
        ownershipGroup,
        ledgers: sortLedgersByName(ownershipMap.get(ownershipGroup) ?? []),
      })).filter((group) => group.ledgers.length > 0),
    }))
    .filter((section) => section.groups.length > 0);
}

export function ledgerCommandSearchValue(ledger: iLedger): string {
  const parts = [
    ledger.code,
    ledger.name,
    ledger.currency,
    ledger.id,
    LEDGER_OWNERSHIP_GROUP_LABEL[getLedgerOwnershipGroup(ledger)],
    ledger.type ? LEDGER_TYPE_LABEL[ledger.type] : "",
  ];
  return parts.filter(Boolean).join(" ");
}
